import React from 'react'
import { WorkPeriod } from '../../api/work-periods'
import { useWorkPeriodEventsQuery } from '../../queries/work-period-events'
import { Field, FieldArray, Formik } from 'formik'
import { Button, Columns, Element, Icon } from 'react-bulma-components'
import SelectField from '../form/fields/select'
import { buildListAsInputOptions } from '../../utils/forms'
import InputField from '../form/fields/input'
import { isEqual } from 'lodash'
import { RemoveIcon } from '../icons'
import { useUpdateMutation } from '../../queries/work-periods'
import { Mission } from '../../api/missions'
import { RequestButton } from '../request-components/request-components'
import WorkPeriodEventModalForm from '../work-period-events/work-period-event-modal-form'
import { buildWorkPeriodEventLabel } from '../../utils/work-period-events'
import Modal, { useModal } from '../modal'
import WorkPeriodDateToDateEventForm from './work-period-date-to-date-event-form'
import { getDocIdAsString } from '../../utils/documents'

interface WorkPeriodEventsFormProps {
  workPeriod: WorkPeriod
  mission: Mission
  disabled?: boolean
}

const WorkPeriodEventsForm: React.FC<WorkPeriodEventsFormProps> = ({
  workPeriod,
  mission,
  disabled,
}) => {
  const workPeriodEventsQuery = useWorkPeriodEventsQuery()
  const updateWorkPeriodMutation = useUpdateMutation()
  const [showWorkPeriodEventForm, setShowWorkPeriodEventForm] = React.useState(false)
  const addEventModal = useModal()

  const initialEvents = (workPeriod.events || []).map(e => ({ ...e, item: e.item._id }))

  return (
    <>
      {!disabled && (
        <WorkPeriodEventModalForm
          setIsDisplayed={setShowWorkPeriodEventForm}
          isDisplayed={showWorkPeriodEventForm}
        />
      )}
      <Formik
        initialValues={{
          events: initialEvents,
        }}
        enableReinitialize
        onSubmit={values => {
          updateWorkPeriodMutation.mutate({
            workPeriodId: getDocIdAsString(workPeriod),
            missionId: getDocIdAsString(workPeriod.mission),
            events: values.events,
          })
        }}
      >
        {({ values, submitForm }) => {
          const isModified = !isEqual(
            values.events.filter((event: any) => event.item),
            initialEvents,
          )
          return (
            <>
              <FieldArray
                name="events"
                render={arrayHelpers => (
                  <Element>
                    {values.events?.[0] ? (
                      values.events.map((event, index) => (
                        <Columns key={index}>
                          <Columns.Column mb={3} size={4}>
                            <Field
                              name={`events.${index}.item`}
                              label="Évènement"
                              component={SelectField}
                              options={buildListAsInputOptions(workPeriodEventsQuery.data || [], {
                                labelBuilder: buildWorkPeriodEventLabel,
                                sortBy: 'code',
                              })}
                              disabled={disabled}
                              help={
                                !values.events?.[index]?.item && (
                                  <Element
                                    renderAs="a"
                                    onClick={() => setShowWorkPeriodEventForm(true)}
                                  >
                                    Créer un nouvel évènement
                                  </Element>
                                )
                              }
                            />
                          </Columns.Column>
                          <Columns.Column size={7}>
                            <Field
                              name={`events.${index}.comment`}
                              component={InputField}
                              label="Commentaire"
                              disabled={disabled}
                            />
                          </Columns.Column>
                          {!disabled && (
                            <Columns.Column key={index} mb={3} size={1} pt={5} textAlign={'center'}>
                              <Button
                                mt={5}
                                size={'small'}
                                onClick={arrayHelpers.handleRemove(index)}
                              >
                                <Icon>
                                  <RemoveIcon />
                                </Icon>
                              </Button>
                            </Columns.Column>
                          )}
                        </Columns>
                      ))
                    ) : (
                      <Element>Aucun évènement lié à la Journée de Travail</Element>
                    )}
                    {!disabled && (
                      <>
                        <Button
                          type="button"
                          onClick={() => addEventModal.setIsDisplayed(true)}
                          mr={2}
                        >
                          Ajouter un évènement
                        </Button>
                        {isModified && (
                          <RequestButton
                            type="button"
                            color={'primary'}
                            onClick={() => submitForm()}
                            mutation={
                              updateWorkPeriodMutation?.variables?.events &&
                              updateWorkPeriodMutation
                            }
                            data-test="save-events-button"
                          >
                            Enregistrer
                          </RequestButton>
                        )}
                      </>
                    )}
                  </Element>
                )}
              />
            </>
          )
        }}
      </Formik>
      {/* Modal for adding events via date range */}
      {!disabled && (
        <Modal actions={addEventModal} title={'Ajouter un évènement'}>
          <WorkPeriodDateToDateEventForm
            mission={mission}
            defaultFromDate={new Date(workPeriod.start.date)}
            defaultToDate={new Date(workPeriod.start.date)}
            onSuccess={() => addEventModal.setIsDisplayed(false)}
          />
        </Modal>
      )}
    </>
  )
}

export default WorkPeriodEventsForm
